import { Component, ComponentInterface, Event, EventEmitter, h, Prop, State, Watch } from '@stencil/core';
import { AlertBlockSize, AlertBlockVariant, DismissDetails, MarginSize } from './jui-alert-block.model';
import { IconSize, IconVariant } from '../jui-icon/jui-icon.model';

@Component({
  tag: 'jui-alert-block',
  styleUrl: 'jui-alert-block.scss',
  shadow: true
})
export class JuiAlertBlock implements ComponentInterface {
  private static ANIMATION_DURATION = 250;

  @Prop({ attribute: 'titleText' })
  titleText = '';

  @Prop({ attribute: 'iconName' })
  iconName: string | null = null;

  @Prop() color: AlertBlockVariant = AlertBlockVariant.info;

  @Prop() size: AlertBlockSize = AlertBlockSize.md;

  @Prop({ attribute: 'marginSize' })
  marginSize: MarginSize = MarginSize.sm;

  @Prop() animated = false;

  @Prop() clearable = false;

  @Prop({ attribute: 'clearAfterMs' })
  clearAfterMs = 0;

  @State() private hidden = false;

  @State() private currentlyHiding = false;

  @Event() public juiDismiss: EventEmitter<DismissDetails>;

  private clearAfterTimeout: NodeJS.Timeout | null = null;
  private hideTimeout: NodeJS.Timeout | null = null;

  private get iconSize(): IconSize {
    switch (this.size) {
      case AlertBlockSize.lg:
        return IconSize.lg;
      case AlertBlockSize.md:
        return IconSize.md;
      default:
        return IconSize.sm;
    }
  }

  private get clearIconSize(): IconSize {
    switch (this.size) {
      case AlertBlockSize.lg:
        return IconSize.md;
      case AlertBlockSize.md:
        return IconSize.sm;
      default:
        return IconSize.xs;
    }
  }

  private hide(closedByUser: boolean) {
    if (this.hidden) return;

    if (!this.animated || matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.hidden = true;
      this.juiDismiss.emit({ closedByUser });
      return;
    }

    this.currentlyHiding = true;

    if (this.hideTimeout !== null) return;
    this.hideTimeout = setTimeout(() => {
      this.hidden = true;
      this.juiDismiss.emit({ closedByUser });
    }, JuiAlertBlock.ANIMATION_DURATION);
  }

  @Watch('clearAfterMs')
  private startClearAfterTimeout() {
    if (this.clearAfterMs <= 0 || this.clearAfterTimeout !== null) {
      return;
    }
    this.clearAfterTimeout = setTimeout(() => {
      this.hide(false);
    }, this.clearAfterMs);
  }

  public componentWillLoad() {
    this.watchHideFlags();
    this.startClearAfterTimeout();
  }

  public disconnectedCallback() {
    if (this.clearAfterTimeout !== null) {
      window.clearTimeout(this.clearAfterTimeout);
    }
    if (this.hideTimeout !== null) {
      window.clearTimeout(this.hideTimeout);
    }
  }

  @Watch('hidden')
  @Watch('currentlyHiding')
  watchHideFlags() {
    // todo check this
    this.render();
  }

  public render(): null | Element {
    if (this.hidden) return null;

    const icon = this.iconName ? (
      <div class="alert-block__icon">
        <jui-icon name={this.iconName} color={IconVariant.current} size={this.iconSize} isButton={false} />
      </div>
    ) : null;

    const title = this.titleText ? <div class="alert-block__title">{this.titleText}</div> : null;

    const closeButton = this.clearable ? (
      <div class="alert-block__icon alert-block__icon--clear">
        <jui-icon
          aria-label="Close info block"
          onClick={() => this.hide(true)}
          name="close"
          color={IconVariant.current}
          size={this.clearIconSize}
          title="Clear alert"
        />
      </div>
    ) : null;

    return (
      <div
        class={{
          'alert-block': true,
          [`alert-block--${this.color}`]: true,
          [`alert-block--${this.size}`]: true,
          [`alert-block--margin-${this.marginSize}`]: true,
          'alert-block--animated': this.animated,
          'alert-block--hiding': this.currentlyHiding
        }}
      >
        {icon}
        <div class="alert-block__main">
          <div class="alert-block__text-content">
            {title}
            <div>
              <slot></slot>
            </div>
          </div>
          {closeButton}
        </div>
      </div>
    );
  }
}
